import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { CreateProduct } from '../ProductStore'
import { Unit, ICompany } from '@getgreenline/homi-shared'
import { ButtonWithTooltip } from '../Components/ButtonWithTooltip'
import { InventoryTrackingButtonGroup } from '../Components/InventoryTrackingButtonGroup'
import { ReactSingleSelect, ISelectOption } from '../../../../../components/ReactSelect'
import { Collapse } from 'reactstrap'
import { THEME_COLORS } from '@getgreenline/shared'

interface AvailableUnits {
  label: string
  value: Unit
}

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  product: CreateProduct
  isEditMode: boolean
}

interface State {
  inventoryExpanded: boolean
}
@inject('currentCompanyStore')
@observer
export class InventorySection extends React.Component<Props, State> {
  state = {
    inventoryExpanded: this.props.isEditMode ? false : true,
  }
  toggleBtnStyle = (isSelected: boolean): string => {
    return isSelected ? 'btn btn-light buttonv3--primary' : 'btn btn-light buttonv3'
  }

  createAvailableUnits = (company: ICompany): AvailableUnits[] => {
    const availableUnits: AvailableUnits[] = [
      {
        label: 'units',
        value: 'unit',
      },
    ]

    if (company.allowBulk) {
      availableUnits.push({
        label: 'grams',
        value: 'gram',
      })
    }

    return availableUnits
  }

  get cardHeaderText() {
    const text = []
    const product = this.props.product
    const hasVariants = product.childProducts.length > 0

    text.push(hasVariants ? 'Has variants' : 'Standalone product')
    text.push(product.inventoryMode ? 'Tracking inventory' : 'No tracking')
    text.push(product.isBatchTracked ? 'Lot tracking' : 'No lot tracking')

    return text.join(' - ')
  }

  confirmProductTypeChange() {
    if (this.props.product.changed) {
      if (window.confirm('All unsaved product details will be discarded. Are you sure?')) {
        return true
      } else {
        return false
      }
    }

    return true
  }

  render() {
    const { product, isEditMode } = this.props
    const company = this.props.currentCompanyStore!.company!

    if (product.needsPricingGroupRefresh) {
      return (
        <div className='card mb-4'>
          <div className='card-body'>
            <h3 className='blaze-text-muted'>
              Please save your product first to edit inventory modes.
            </h3>
          </div>
        </div>
      )
    }

    const availableUnits = this.createAvailableUnits(company)

    const infoIcon = 'blaze-text-muted ml-2'
    const hasVariants = product.childProducts.length > 0
    const { inventoryExpanded } = this.state

    return (
      <>
        <div className='card mb-4'>
          <div style={{ border: inventoryExpanded ? '' : 0 }} className='card-header d-flex'>
            <div className='mr-md-0 mr-lg-4'>Inventory settings</div>
            <div style={{ flexGrow: 1 }}>{this.cardHeaderText}</div>
            <a onClick={() => this.setState({ inventoryExpanded: !inventoryExpanded })}>
              {inventoryExpanded ? 'Collapse' : 'Expand'}
            </a>
          </div>
          <Collapse isOpen={inventoryExpanded}>
            <div className='card-body'>
              {company.allowBulk && (
                <div className='form-group row card__body__text align-items-center'>
                  <div className='col-lg-4'>
                    <label className='mb-2 mr-2'>Unit of measurement</label>
                  </div>
                  <div className='col-lg-4'>
                    <ReactSingleSelect
                      initialValue={product.unit}
                      options={availableUnits}
                      onChange={(option: ISelectOption | null) => {
                        if (option !== null) {
                          product.setUnit(option.value as Unit)
                        }
                      }}
                      isDisabled={product.isDeleted}
                    />
                  </div>
                </div>
              )}
              <div className='form-group row card__body__text align-items-center'>
                <div className='col-lg-4'>
                  <label>Variants</label>
                  <div className='blaze-text-muted mb-2'>
                    This cannot be changed after the product is created
                  </div>
                </div>
                <div className='col-lg-8'>
                  <div style={{ display: 'flex', flexFlow: 'row' }}>
                    <button
                      className={`${this.toggleBtnStyle(hasVariants)} buttonv3--btn-lg`}
                      onClick={() => {
                        this.confirmProductTypeChange() && product.setIsMasterProduct(true)
                      }}
                      style={{ marginRight: '.25rem' }}
                      disabled={isEditMode}
                    >
                      <svg
                        height='512pt'
                        viewBox='-21 0 512 512'
                        style={{ width: '2rem', height: '2rem', fill: THEME_COLORS.BRAND.blackAlt }}
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path d='m186.667969 416c-49.984375 0-90.667969-40.683594-90.667969-90.667969v-218.664062h-37.332031c-32.363281 0-58.667969 26.300781-58.667969 58.664062v288c0 32.363281 26.304688 58.667969 58.667969 58.667969h266.664062c32.363281 0 58.667969-26.304688 58.667969-58.667969v-37.332031zm0 0' />
                        <path d='m469.332031 58.667969c0-32.40625-26.261719-58.667969-58.664062-58.667969h-224c-32.40625 0-58.667969 26.261719-58.667969 58.667969v266.664062c0 32.40625 26.261719 58.667969 58.667969 58.667969h224c32.402343 0 58.664062-26.261719 58.664062-58.667969zm0 0' />
                      </svg>
                      <div>This product has variants</div>
                    </button>

                    <button
                      className={`${this.toggleBtnStyle(!hasVariants)} buttonv3--btn-lg`}
                      onClick={() => {
                        this.confirmProductTypeChange() && product.setIsMasterProduct(false)
                      }}
                      style={{ marginLeft: '.25rem' }}
                      disabled={isEditMode}
                    >
                      <span className='fa fa-square fa-2x blaze-text-muted mb-2' />
                      <div>This product is standalone</div>
                    </button>
                  </div>
                </div>
              </div>
              {!isEditMode && product.pricingGroupId ? (
                <div className='form-group row card__body__text align-items-center'>
                  <div className='col-lg-4'>
                    <label>Inventory tracking</label>
                    <div className='blaze-text-muted'>All cannabis products must be tracked.</div>
                  </div>
                  <div className='col-lg-4'>
                    <div className='mb-2 blaze-text-muted'>
                      <i className='mr-2 fa fa-exclamation-triangle blaze-text-warning' />
                      <span>Settings are carried over from your pricing group. </span>
                      <a
                        target='__blank'
                        href={`/company/${company.id}/dashboard/products/pricingGroups`}
                      >
                        View pricing groups
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <InventoryTrackingButtonGroup
                    product={product}
                    company={company}
                    infoIcon={infoIcon}
                    isEditMode={isEditMode}
                    toggleBtnStyle={this.toggleBtnStyle}
                  />

                  {product.inventoryMode === 'bulk' && product.unit === 'gram' && (
                    <div className='form-group row card__body__text align-items-center'>
                      <div className='col-lg-4'>
                        <label>Bulk pricing mode</label>
                        <div className='blaze-text-muted mb-2'>
                          Unit of measurement must be in grams
                        </div>
                      </div>
                      <div className='col-lg-8'>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ marginRight: '0.25rem', width: '100%' }}>
                            <ButtonWithTooltip
                              className={`${this.toggleBtnStyle(!product.inBetweenPricing)} mr-2 `}
                              tooltipIconClassName={infoIcon}
                              buttonLabel='Highest weight price'
                              disabled={!!product.pricingGroupId || product.isDeleted}
                              tooltipContent={
                                <div className='text-left'>
                                  <div>Default</div>
                                  <div>Example: Sell 8 grams</div>
                                  <div>2.3 units of 3.5 grams</div>
                                </div>
                              }
                              effect='float'
                              onClick={() => product.setInBetweenPricing(false)}
                            />
                          </div>
                          <div style={{ marginLeft: '0.25rem', width: '100%' }}>
                            <ButtonWithTooltip
                              className={`${this.toggleBtnStyle(product.inBetweenPricing)}`}
                              tooltipIconClassName={infoIcon}
                              buttonLabel='Multiple weight prices'
                              disabled={!!product.pricingGroupId || product.isDeleted}
                              tooltipContent={
                                <div className='text-left'>
                                  <div>Default</div>
                                  <div>Example: Sell 8 grams</div>
                                  <div>2 units of 3.5 grams + 1 unit of 1 gram</div>
                                </div>
                              }
                              effect='float'
                              onClick={() => product.setInBetweenPricing(true)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Collapse>
        </div>
      </>
    )
  }
}
